import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  type LucideIcon,
  Sparkles,
  TreePine,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  SCHEDULE_E_CATEGORIES,
  TAX_ADVANTAGE_GROUPS,
  type TaxAdvantageGroup,
  type TaxAdvantageItem,
  useAccounting,
} from "@/hooks/use-accounting";
import {
  categoryIcon,
  fmtMoney,
  fmtMoneyShort,
  TONE_MARKER,
  TONE_PILL,
} from "@/components/accounting/accounting-format";
import { cn } from "@/lib/utils";

interface Props {
  year: number;
}

const GROUP_ICON: Record<TaxAdvantageGroup["icon"], LucideIcon> = {
  check: CheckCircle2,
  lightbulb: Lightbulb,
  warning: AlertTriangle,
  tree: TreePine,
};

const TAG_ICON: Record<NonNullable<TaxAdvantageItem["tagIcon"]>, LucideIcon> = {
  warning: AlertTriangle,
  sparkle: Sparkles,
};

export function FinancialOverview({ year }: Props) {
  const { allTransactions, scheduleE, totalIncome, totalExpenses, netIncome, properties } =
    useAccounting(year);

  const expenseCount = allTransactions.filter((t) => t.type === "expense").length;
  const autoCount = allTransactions.filter((t) => t.source !== "Manual").length;
  const manualCount = allTransactions.length - autoCount;

  // Monthly income / expenses / net.
  const monthlyData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months.map((m, i) => {
      const prefix = `${year}-${String(i + 1).padStart(2, "0")}`;
      const monthTxns = allTransactions.filter((t) => t.date.startsWith(prefix));
      const income = monthTxns.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
      const expenses = monthTxns.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
      return { month: m, income, expenses, net: income - expenses };
    });
  }, [allTransactions, year]);

  // Top deductions by Schedule E expense bucket.
  const topDeductions = useMemo(() => {
    const total = SCHEDULE_E_CATEGORIES.filter((c) => c.type === "expense").reduce(
      (s, c) => s + (scheduleE[c.key] || 0),
      0,
    );
    return SCHEDULE_E_CATEGORIES.filter((c) => c.type === "expense" && (scheduleE[c.key] || 0) > 0)
      .map((c) => ({ ...c, amount: scheduleE[c.key], pct: total ? Math.round((scheduleE[c.key] / total) * 100) : 0 }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [scheduleE]);

  const kpis = [
    {
      label: "Rental income",
      value: fmtMoney(totalIncome),
      tone: "text-success",
      sub: `Schedule E line 3 · across ${properties.length} ${properties.length === 1 ? "property" : "properties"}`,
    },
    {
      label: "Deductible expenses",
      value: fmtMoney(totalExpenses),
      tone: "text-destructive",
      sub: `Schedule E lines 5-19 · ${expenseCount} entries`,
    },
    {
      label: "Net rental income",
      value: fmtMoney(netIncome),
      tone: netIncome >= 0 ? "text-success" : "text-destructive",
      sub: "Schedule E line 26 · taxable amount",
    },
    {
      label: "Transactions",
      value: String(allTransactions.length),
      tone: "text-foreground",
      sub: `${autoCount} auto · ${manualCount} manual`,
    },
  ];

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4">
            <p className="text-xs font-semibold text-muted-foreground">{k.label}</p>
            <p className={cn("mt-2 text-2xl font-extrabold tabular-nums leading-none", k.tone)}>{k.value}</p>
            <p className="mt-1.5 text-[11px] text-muted-foreground/80">{k.sub}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
        {/* Income vs expenses */}
        <Card className="p-5">
          <div className="mb-3">
            <h3 className="text-sm font-bold text-foreground">Income vs. expenses</h3>
            <p className="text-xs text-muted-foreground">{year} · monthly breakdown across all properties</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 4" className="stroke-border" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={52} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(v: number, name: string) => [fmtMoney(v), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
                <Area type="monotone" dataKey="income" stroke="none" fill="hsl(var(--success))" fillOpacity={0.1} legendType="none" />
                <Area type="monotone" dataKey="expenses" stroke="none" fill="hsl(var(--destructive))" fillOpacity={0.1} legendType="none" />
                <Line type="monotone" dataKey="income" name="Income" stroke="hsl(var(--success))" strokeWidth={2.25} dot={{ r: 2.5 }} />
                <Line type="monotone" dataKey="expenses" name="Expenses" stroke="hsl(var(--destructive))" strokeWidth={2.25} dot={{ r: 2.5 }} />
                <Line type="monotone" dataKey="net" name="Net" stroke="hsl(217 91% 60%)" strokeWidth={2.25} dot={{ r: 2.5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top deductions */}
        <Card className="p-5">
          <div className="mb-3">
            <h3 className="text-sm font-bold text-foreground">Top deductions</h3>
            <p className="text-xs text-muted-foreground">Largest expense buckets this year</p>
          </div>
          {topDeductions.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              No deductions tracked for {year} yet.
            </p>
          ) : (
            <div className="flex flex-col">
              {topDeductions.map((d) => {
                const Icon = categoryIcon(d.key);
                return (
                  <div key={d.key} className="grid grid-cols-[24px_1fr_auto] items-center gap-3 border-b border-border py-2.5 last:border-b-0">
                    <span className="grid h-6 w-6 place-items-center rounded-md bg-primary/10 text-primary">
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold text-foreground">{d.label}</p>
                      <p className="text-[11px] text-muted-foreground">Schedule E line {d.line} · {d.pct}% of expenses</p>
                    </div>
                    <p className="text-sm font-bold tabular-nums text-foreground">{fmtMoney(d.amount)}</p>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Real-estate tax advantages */}
      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-[44px_1fr] items-center gap-3.5 border-b border-border p-5">
          <span className="grid h-11 w-11 place-items-center rounded-lg bg-success/15 text-success">
            <Lightbulb className="h-5 w-5" />
          </span>
          <div>
            <h3 className="text-base font-bold text-foreground">Real-estate tax advantages</h3>
            <p className="text-xs text-muted-foreground">Key deductions, strategies, and shelters most rental owners can use</p>
          </div>
        </div>

        <div className="px-5">
          {TAX_ADVANTAGE_GROUPS.map((group) => {
            const GroupIcon = GROUP_ICON[group.icon];
            return (
              <div key={group.key} className="border-b border-border py-4 last:border-b-0">
                <span className={cn("inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider", TONE_PILL[group.tone])}>
                  <GroupIcon className="h-3 w-3" />
                  {group.title}
                </span>
                <p className="mb-1.5 mt-1.5 text-xs text-muted-foreground">{group.subtitle}</p>
                <div className="flex flex-col">
                  {group.items.map((item) => {
                    const TagIcon = item.tagIcon ? TAG_ICON[item.tagIcon] : null;
                    return (
                      <div key={item.name} className="grid grid-cols-[14px_1fr_auto] items-start gap-3 border-t border-border py-3 first:border-t-0">
                        <span className="mt-0.5 h-3.5 w-3.5 rounded-[4px] border border-input p-0.5">
                          <span className={cn("block h-full w-full rounded-[2px]", TONE_MARKER[item.tone])} />
                        </span>
                        <div className="min-w-0">
                          <p className="mb-1 text-[13.5px] font-bold text-foreground">{item.name}</p>
                          <p className="text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                        </div>
                        <span className={cn("inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10.5px] font-bold", TONE_PILL[item.tone])}>
                          {TagIcon ? <TagIcon className="h-2.5 w-2.5" /> : null}
                          {item.tag}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border bg-muted/40 px-5 py-3.5">
          <p className="max-w-[460px] text-xs text-muted-foreground">
            General reference only — consult a CPA for application to your specific situation.
          </p>
        </div>
      </Card>
    </div>
  );
}
